/**
 * Selection-toolbar drag handle — press-and-drag moves the current
 * selection. A plain View (NOT a Pressable — the pan gesture owns the
 * touch) wrapped in a GestureDetector around the PREBUILT engine gesture:
 * the compiled chrome never authors gesture worklets (React Compiler
 * safety), it just mounts what `useFloorEditor` hands it.
 *
 * @module FloorPlanEditorSelectionToolbarDragHandle
 * @category Containers
 */

import { View } from 'react-native';
import type { PanGesture } from 'react-native-gesture-handler';
import { GestureDetector } from 'react-native-gesture-handler';
import { Move } from 'lucide-react-native';

import { Icon } from '@components/ui/icon';

type Props = {
  /** Prebuilt selection-drag pan from `useFloorEditor.selectionDragGesture`. */
  gesture: PanGesture;
  /** Translated accessibility label ("Drag to move"). */
  label: string;
};

/**
 * Selection-toolbar drag handle — press-and-drag moves the current
 * selection, mounting the prebuilt engine gesture (never authoring gesture
 * worklets in compiled chrome).
 *
 * @category Containers
 * @param gesture - Prebuilt selection-drag pan from the engine.
 * @param label - Translated accessibility label.
 */
const SelectionToolbarDragHandle = ({ gesture, label }: Props) => (
  <GestureDetector gesture={gesture}>
    <View
      collapsable={false}
      accessibilityRole="button"
      accessibilityLabel={label}
      className="bg-primary/15 h-10 w-10 items-center justify-center rounded-lg">
      <Icon as={Move} size={18} className="text-primary" />
    </View>
  </GestureDetector>
);

export default SelectionToolbarDragHandle;
